import { useContext, useMemo } from "react";
import styled from "styled-components";
import { FiEdit2, FiTrash2 } from "react-icons/fi";

import { formatarData } from "../../Funcoes/FormatarData";
import { formatValorMoney } from "../../Funcoes/FormatValorMoney";
import { VerifyPermission } from "../../Funcoes/VerifyPermission";
import { AuthContext } from "../../Auth/AuthProvider";
import { useModal } from "../../contexts/ModalContext";

import InfoCard from "./InfoCard";
import ModalEditFolga from "../Colaborador/ModalEditFolga";
import ModalDeleteFolga from "../Colaborador/ModalDeleteFolga";

const TableWrapper = styled.div`
    width: 100%;
    overflow-x: auto;

    @media (max-width: 768px) {
        display: none;
    }
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;

    thead th {
        text-align: left;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.3px;
        color: #ACAFB0;
        text-transform: uppercase;
        padding: 8px 12px;
        border-bottom: 2px solid #e2e2e2;
        white-space: nowrap;
    }

    tbody td {
        font-size: 13px;
        color: #263B4B;
        padding: 10px 12px;
        border-bottom: 1px solid #ececec;
        white-space: nowrap;
    }

    tbody tr:last-child td {
        border-bottom: none;
    }

    tbody tr:hover td {
        background-color: #f3f5f6;
    }
`;

const CardsList = styled.div`
    display: none;

    @media (max-width: 768px) {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }
`;

const FolgaItem = styled.div`
    background-color: #FAFCFC;
    border: 1px solid #ececec;
    border-radius: 12px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const FolgaLine = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 13px;

    & span:first-child {
        color: #ACAFB0;
        font-weight: 700;
        text-transform: uppercase;
        font-size: 11px;
    }

    & span:last-child {
        color: #263B4B;
        font-weight: 600;
        text-align: right;
    }
`;

const Empty = styled.div`
    text-align: center;
    color: #ACAFB0;
    font-size: 13px;
    font-weight: 600;
    padding: 16px;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const Acoes = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    justify-content: ${(p) => (p.$mobile ? "flex-end" : "flex-start")};
`;

const BotaoAcao = styled.button`
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    color: ${(p) => (p.$danger ? "#b91c1c" : "#1d4ed8")};
    background-color: ${(p) => (p.$danger ? "rgba(185, 28, 28, 0.12)" : "rgba(29, 78, 216, 0.10)")};
    border: 1px solid transparent;

    &:hover {
        background-color: ${(p) => (p.$danger ? "rgba(185, 28, 28, 0.22)" : "rgba(29, 78, 216, 0.20)")};
        border-color: ${(p) => (p.$danger ? "rgba(185, 28, 28, 0.5)" : "rgba(29, 78, 216, 0.45)")};
    }

    &:focus-visible {
        outline: 2px solid ${(p) => (p.$danger ? "#b91c1c" : "#1d4ed8")};
        outline-offset: 2px;
    }
`;

const money = (valor) => "R$ " + (valor ? formatValorMoney(valor) : formatValorMoney("0.00"));

const FolgasCard = ({ folgasCampo, fetchData, protheus }) => {

    const { user } = useContext(AuthContext);
    const { isModalOpen, openModal } = useModal();

    const podeEditar = VerifyPermission(user, {
        modulo: "rh",
        submodulo: "colaborador",
        funcao: "edit_last_folga"
    });

    const podeApagar = VerifyPermission(user, {
        modulo: "rh",
        submodulo: "colaborador",
        funcao: "delete_last_folga"
    });

    const mostrarAcoes = podeEditar || podeApagar;

    const ultimaFolgaEditavel = useMemo(() => {
        if (!folgasCampo || folgasCampo.length === 0) return null;

        const ordenadas = [...folgasCampo].sort((a, b) => {
            if (a.data === b.data) return (b.id || 0) - (a.id || 0);
            return (b.data || '').localeCompare(a.data || '');
        });

        const ultima = ordenadas[0];
        const now = new Date();
        const hoje = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

        return ultima?.data && ultima.data > hoje ? ultima : null;
    }, [folgasCampo]);

    const abrirEditFolga = () => openModal('editLastFolga');
    const abrirDeleteFolga = () => openModal('deleteLastFolga');

    const temFolgas = folgasCampo?.length > 0;

    const renderAcoes = (item, mobile = false) => {
        const isUltimaEditavel = ultimaFolgaEditavel && ultimaFolgaEditavel.id === item.id;
        if (!mostrarAcoes || !isUltimaEditavel) return null;

        return (
            <Acoes $mobile={mobile}>
                {podeEditar && (
                    <BotaoAcao type="button" onClick={abrirEditFolga} title="Editar">
                        <FiEdit2 size={14} />
                    </BotaoAcao>
                )}
                {podeApagar && (
                    <BotaoAcao type="button" $danger onClick={abrirDeleteFolga} title="Excluir">
                        <FiTrash2 size={14} />
                    </BotaoAcao>
                )}
            </Acoes>
        );
    };

    return (
        <>
            <InfoCard title="FOLGAS DE CAMPO" grid={false}>
                {temFolgas ? (
                    <>
                        <TableWrapper>
                            <Table>
                                <thead>
                                    <tr>
                                        <th>Valor</th>
                                        <th>Data</th>
                                        <th>Retorno</th>
                                        <th>Dias gozo</th>
                                        <th>Data de pagamento</th>
                                        {mostrarAcoes && <th style={{ width: 90 }} />}
                                    </tr>
                                </thead>
                                <tbody>
                                    {folgasCampo.map(item => (
                                        <tr key={item.id}>
                                            <td>{money(item?.valor)}</td>
                                            <td>{item?.data ? formatarData(item.data) : '-'}</td>
                                            <td>{item?.data_retorno ? formatarData(item.data_retorno) : '-'}</td>
                                            <td>{item.dias_gozo}</td>
                                            <td>{item?.data_pagamento ? formatarData(item.data_pagamento) : '-'}</td>
                                            {mostrarAcoes && <td>{renderAcoes(item)}</td>}
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </TableWrapper>

                        <CardsList>
                            {folgasCampo.map(item => (
                                <FolgaItem key={item.id}>
                                    <FolgaLine><span>Valor</span><span>{money(item?.valor)}</span></FolgaLine>
                                    <FolgaLine><span>Data</span><span>{item?.data ? formatarData(item.data) : '-'}</span></FolgaLine>
                                    <FolgaLine><span>Retorno</span><span>{item?.data_retorno ? formatarData(item.data_retorno) : '-'}</span></FolgaLine>
                                    <FolgaLine><span>Dias gozo</span><span>{item.dias_gozo}</span></FolgaLine>
                                    <FolgaLine><span>Data de pagamento</span><span>{item?.data_pagamento ? formatarData(item.data_pagamento) : '-'}</span></FolgaLine>
                                    {renderAcoes(item, true)}
                                </FolgaItem>
                            ))}
                        </CardsList>
                    </>
                ) : (
                    <Empty>COLABORADOR NÃO POSSUÍ FOLGAS DE CAMPO</Empty>
                )}
            </InfoCard>

            {isModalOpen('editLastFolga') && ultimaFolgaEditavel && podeEditar && (
                <ModalEditFolga
                    folga={ultimaFolgaEditavel}
                    fetchData={fetchData}
                    protheus={protheus}
                />
            )}

            {isModalOpen('deleteLastFolga') && ultimaFolgaEditavel && podeApagar && (
                <ModalDeleteFolga
                    folga={ultimaFolgaEditavel}
                    fetchData={fetchData}
                />
            )}
        </>
    )
}

export default FolgasCard;
